<template>
  <div class="renderPage nde-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="nde-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-file-document-edit-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase nde-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Notas de Débito
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="nde-btn font-weight-bold" :disabled="!incluirValid" @click="validarLicencia()">
        <v-icon left small>mdi-plus</v-icon> Nueva Nota
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="nde-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="auto" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3 pt-2 pb-3">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-dialog ref="dialog" v-model="dateModal" :return-value.sync="dates" persistent width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="datesFormat"
                  label="Rango de fechas"
                  prepend-inner-icon="mdi-calendar"
                  readonly outlined dense hide-details
                  class="nde-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" range scrollable color="primary">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateModal = false">Cancelar</v-btn>
                <v-btn text color="primary" @click="$refs.dialog.save(dates); setDatesFormat(); getData();">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" sm="5" md="5">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar por cliente, código o estado..."
              single-line hide-details outlined dense
              class="nde-search"
            ></v-text-field>
          </v-col>
          <v-spacer />
          <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-row>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :loading="visibleProgressTable"
        class="nde-table"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.estadoHacienda`]="{ item }">
          <v-chip small :color="getStatusColor(item.estadoHacienda)" text-color="white" class="font-weight-bold">
            {{ item.estadoHacienda }}
          </v-chip>
        </template>
        <template v-slot:[`item.totalComprobante`]="{ item }">
          <span class="font-weight-bold">{{ (item.totalComprobante || 0).toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }) }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark style="min-width:36px;height:36px;padding:0;border-radius:4px" @click.stop="editItem(item)">
            <v-icon size="18">mdi-eye-outline</v-icon>
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-file-question-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron notas de débito.</p>
            <v-btn color="primary" depressed small @click="getData">Refrescar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- FORM DRAWER -->
    <v-navigation-drawer v-model="drawerForm" fixed right temporary stateless
      :width="$vuetify.breakpoint.mdAndUp ? 'calc(100% - 290px)' : '100%'"
      class="nde-drawer">
      <div class="d-flex flex-column fill-height">
        <!-- HEADER -->
        <div class="nde-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'nde-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-file-document-edit-outline</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex === -1 ? 'Nueva Nota de Débito' : 'Detalles de Nota de Débito' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">
              {{ editedItem.codigoFact ? 'Comprobante #' + editedItem.codigoFact : 'Generando nuevo documento...' }}
              <v-chip v-if="editedIndex > -1 && editedItem.estadoHacienda" x-small dark outlined class="ml-2 font-weight-black" style="height:18px">
                {{ editedItem.estadoHacienda }}
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- CONTENIDO DEL DRAWER -->
        <div class="flex-grow-1 overflow-y-auto px-2 py-2 bg-light">
          <v-form v-model="valid" ref="form">
            <v-expansion-panels v-model="activePanels" multiple class="modern-panels">
              
              <!-- SECCIÓN 1: DOCUMENTO ORIGEN -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <v-icon color="primary" left class="mr-3">mdi-file-link-outline</v-icon>
                  Documento de Referencia
                </v-expansion-panel-header>
                <v-expansion-panel-content class="panel-content-compact">
                  <v-row dense align="center">
                    <v-col cols="12" md="7">
                      <v-text-field
                        v-model="editedItem.codigoDev"
                        label="Clave Hacienda del Documento Origen"
                        outlined dense hide-details
                        readonly
                        :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                        prepend-inner-icon="mdi-key-variant"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-btn
                        color="primary" depressed block
                        height="40"
                        @click="mostrarBuscarDocumentos"
                        :disabled="!btnAgregarDocumento"
                        class="font-weight-bold"
                      >
                        <v-icon left>mdi-file-search</v-icon>
                        Buscar
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-3" v-if="editedItem.emisorNombre">
                    <v-col cols="12" md="6">
                      <v-text-field v-model="editedItem.emisorNombre" label="Emisor Autorizado" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-office-building"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="editedItem.moneda" label="Moneda" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-cash-multiple"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="editedItem.documentoOrigen" label="Origen" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-file-account"></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 2: RECEPTOR -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <v-icon color="primary" left class="mr-3">mdi-account-details-outline</v-icon>
                  Información del Receptor
                </v-expansion-panel-header>
                <v-expansion-panel-content class="panel-content-compact">
                  <v-row dense>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="editedItem.clienteCod" label="# Cliente" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="editedItem.clienteCedula" label="Cédula" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="editedItem.clienteNombre" label="Nombre/Razón Social" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="editedItem.clientecorreo" label="Correo Notificación" outlined dense hide-details prepend-inner-icon="mdi-email-outline"></v-text-field>
                    </v-col>

                    <v-col cols="12" v-if="actividadesCliente.length > 0">
                      <v-select
                        v-model="editedItem.clienteCodigoActividad"
                        :items="actividadesCliente"
                        item-text="descripcion"
                        item-value="codigo"
                        label="Actividad Económica del Receptor"
                        outlined dense hide-details
                        prepend-inner-icon="mdi-briefcase-outline"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 3: LÍNEAS DE DETALLE -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <v-icon color="primary" left class="mr-3">mdi-format-list-bulleted</v-icon>
                  Líneas de la Nota
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-0 panel-content-compact">
                      <v-data-table
                        :headers="headersDetalle"
                        :items="lineasDetalle"
                        hide-default-footer
                        class="detail-table-modern border-light rounded-xl overflow-hidden"
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.cantidad`]="{ item }">
                           <span class="font-weight-bold">{{ item.cantidad }}</span>
                        </template>
                        <template v-slot:[`item.montoStr`]="{ item }">
                          <span class="font-weight-bold">{{ item.montoStr }}</span>
                        </template>
                        <template v-slot:[`item.totalLineaStr`]="{ item }">
                          <span class="font-weight-black primary--text">{{ item.totalLineaStr }}</span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                          <v-btn icon small color="primary" @click="detalleEditItem(item)" v-if="guardarValid">
                            <v-icon small>mdi-pencil-outline</v-icon>
                          </v-btn>
                          <v-btn icon small color="error" @click="deleteItem(item)" v-if="guardarValid">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 4: TOTALES -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <v-icon color="primary" left class="mr-3">mdi-calculator</v-icon>
                  Cálculos y Totales
                </v-expansion-panel-header>
                <v-expansion-panel-content class="panel-content-compact">
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                        <div class="caption grey--text font-weight-bold text-uppercase mb-1">Subtotal</div>
                        <div class="text-body-2 font-weight-bold">{{ editedItem.subtotalStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                        <div class="caption grey--text font-weight-bold text-uppercase mb-1">Impuestos</div>
                        <div class="text-body-2 font-weight-bold primary--text">{{ editedItem.totalImpuestoStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                        <div class="caption grey--text font-weight-bold text-uppercase mb-1">Descuentos</div>
                        <div class="text-body-2 font-weight-bold error--text">{{ editedItem.totalDescuentosStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="rounded-xl pa-2 primary border-light text-center white--text shadow-primary">
                        <div class="caption opacity-80 mb-0 uppercase">TOTAL NOTA</div>
                        <div class="text-body-2 font-weight-black">{{ editedItem.totalComprobanteStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 5: ESTADO HACIENDA -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <div class="d-flex align-center" style="width:100%">
                    <v-icon color="primary" left class="mr-2">mdi-cloud-check-outline</v-icon>
                    Estado y Gestión Hacienda
                    <v-spacer></v-spacer>
                    <v-chip
                      v-if="editedItem.estadoHacienda"
                      :color="getEstadoColor(editedItem.estadoHacienda)"
                      dark x-small
                      class="font-weight-bold mr-2"
                    >
                      {{ editedItem.estadoHacienda }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense align="start">
                    <v-col cols="12" md="8">
                      <v-text-field
                        v-model="editedItem.clavehacienda"
                        label="Clave de Comprobante"
                        readonly outlined dense hide-details
                        :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <v-btn
                        color="primary"
                        depressed block height="40"
                        @click="consultarEstadoHacienda"
                        :disabled="!consultarEstadoValid || visibleProgress"
                        :loading="visibleProgress"
                        class="font-weight-bold"
                      >
                        <v-icon left>mdi-sync</v-icon>
                        Consultar Estado
                      </v-btn>
                    </v-col>
                    <v-col cols="12" v-if="editedItem.estadoHacienda" class="mt-2">
                      <div class="d-flex align-center pa-2 rounded-lg bg-light border-light">
                        <v-icon :color="getEstadoColor(editedItem.estadoHacienda)" class="mr-2">mdi-information-outline</v-icon>
                        <span class="font-weight-bold mr-2">Estado:</span>
                        <v-chip dark small :color="getEstadoColor(editedItem.estadoHacienda)" class="font-weight-black">
                          {{ editedItem.estadoHacienda }}
                        </v-chip>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editedItem.mensajeHacienda"
                        label="Respuesta de Hacienda"
                        readonly outlined rows="2"
                        class="mt-2"
                        hide-details
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <v-row v-if="editedIndex > -1" class="border-top-light pt-4 px-2 mt-2" justify="center">
                    <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getReport" :disabled="!verPDF">
                      <v-icon left>mdi-file-pdf-box</v-icon> Ver PDF
                    </v-btn>
                    <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getXMLFirmado" :disabled="!verXMLFirmado">
                      <v-icon left>mdi-xml</v-icon> XML Enviado
                    </v-btn>
                    <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getXMLRespuesta" :disabled="!verXMLRespuesta">
                      <v-icon left>mdi-message-reply-text</v-icon> Respuesta XML
                    </v-btn>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 6: PARÁMETROS DE EMISIÓN -->
              <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                <v-expansion-panel-header class="font-weight-bold primary--text">
                  <div class="d-flex align-center" style="width:100%">
                    <v-icon color="primary" left class="mr-2">mdi-cog-outline</v-icon>
                    Parámetros de Emisión
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-select label="Condición Venta" v-model="editedItem.condicionVenta" :items="condicionesVenta" item-text="descripcion" item-value="codigo" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select label="Medio de Pago" v-model="editedItem.medioPago" :items="mediosPago" item-text="descripcion" item-value="codigo" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editedItem.codigoActividad"
                        :items="actividades"
                        item-text="descripcion"
                        item-value="codigo"
                        label="Actividad Económica del Emisor"
                        outlined dense hide-details
                        prepend-inner-icon="mdi-briefcase-outline"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="editedItem.ordenCompra" label="Orden de Compra #" outlined dense prepend-inner-icon="mdi-file-document-outline" hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="editedItem.observacion" label="Observaciones" outlined rows="2" hide-details></v-textarea>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </div>

        <!-- FOOTER ACCIONES -->
        <div class="nde-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'nde-drawer-footer--dark' : ''">
          <v-btn v-if="reenviarValid" color="secondary" text class="nde-btn font-weight-bold" @click="reenviar">
            <v-icon left small>mdi-email-outline</v-icon> Reenviar
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold nde-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold nde-btn" :loading="visibleProgress" @click="save" :disabled="!guardarValid">
            {{ editedIndex === -1 ? 'GENERAR NOTA' : 'GUARDAR CAMBIOS' }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- VISOR DE DOCUMENTOS (PDF / XML) -->
    <v-dialog v-model="docViewerDialog" max-width="1100" eager>
      <v-card style="height: 88vh;" class="d-flex flex-column overflow-hidden rounded-xl">
        <div class="doc-viewer-toolbar d-flex align-center px-3 py-2 white--text flex-shrink-0">
          <v-icon color="white" size="18" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="font-weight-bold text-subtitle-2">{{ docViewerTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small dark class="mr-1" @click="abrirEnNuevaPestana" title="Abrir en nueva pestaña">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon x-small dark @click="docViewerDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <iframe
          v-if="docViewerDialog"
          :src="docViewerUrl"
          class="flex-grow-1"
          style="border: none; width: 100%;"
        ></iframe>
      </v-card>
    </v-dialog>

    <!-- MODAL DE BÚSQUEDA DE DOCUMENTOS -->
    <BuscarDocumentosND 
      ref="buscarDocs"
      :dialog="btnAgregarDocumento"
      @clicked="selected"
      @closed="btnAgregarDocumento = false"
    />

    <!-- MODAL DE EDICIÓN DE ARTÍCULO (SIMILAR A FACTURA) -->
    <v-dialog v-model="dialogArticulos" max-width="600px" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <v-toolbar flat color="primary" dark class="px-2">
          <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-pencil-box-outline</v-icon>
            Detalle del Artículo
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="detalleEditedItem.descripcion"
                label="Descripción"
                outlined
                readonly
                :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                prepend-inner-icon="mdi-format-text"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="detalleEditedItem.cantidad"
                label="Cantidad"
                type="number"
                min="0"
                :max="detalleEditedItem.cantidadMax"
                outlined
                prepend-inner-icon="mdi-numeric"
                @input="changeCantidad"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="detalleEditedItem.montoStr"
                label="Precio Unitario"
                outlined
                readonly
                :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                prepend-inner-icon="mdi-cash"
              ></v-text-field>
            </v-col>

            <!-- CAMPOS FARMACÉUTICOS (PARA RESOLVER ERROR SQL) -->
            <v-col cols="12" class="pt-2">
              <v-divider class="mb-4"></v-divider>
              <div class="subtitle-2 blue--text mb-2 font-weight-bold">Información Farmacéutica</div>
            </v-col>
            
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="detalleEditedItem.registroMedicamento"
                label="Registro Medicamento"
                outlined
                dense
                prepend-inner-icon="mdi-pill"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12" sm="6">
              <v-select
                v-model="detalleEditedItem.formaFarmaceutica"
                :items="formas"
                item-text="nombre"
                item-value="codigo"
                label="Forma Farmacéutica"
                outlined
                dense
                prepend-inner-icon="mdi-medical-bag"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text large class="px-6 font-weight-bold grey--text" @click="close">Cancelar</v-btn>
          <v-btn
            color="primary" large elevation="4"
            class="px-10 font-weight-bold shadow-primary"
            @click="saveDetalleArticulo"
          >
            CONFIRMAR
            <v-icon right small>mdi-check-circle-outline</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MODAL REENVÍO POR CORREO -->
    <v-dialog v-model="dialogFormaReenvio" max-width="500px">
      <v-card class="rounded-xl overflow-hidden">
        <div class="secondary pa-6 white--text d-flex align-center">
          <v-icon color="white" class="mr-3">mdi-email-send-outline</v-icon>
          <h3 class="text-h6 font-weight-bold">Reenviar Comprobante</h3>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogFormaReenvio = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="pa-6 mt-4">
          <p class="body-1 mb-4">Confirme o edite el correo electrónico para el reenvío:</p>
          <v-text-field
            v-model="reenvioInfo.correo"
            label="Correo Electrónico"
            outlined
            dense
           
            prepend-inner-icon="mdi-email-outline"
            hide-details
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer></v-spacer>
          <v-btn text class="px-6" @click="dialogFormaReenvio = false">Cancelar</v-btn>
          <v-btn color="secondary" depressed class="px-8 font-weight-bold" @click="confirmarReenvio">
            Enviar Ahora
            <v-icon right small>mdi-send</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";
import BuscarDocumentosND from "./BuscarDocumentosND.vue";

export default {
  components: {
    Snackbar,
    BuscarDocumentosND,
  },
  data() {
    return {
      drawerForm: false,
      visibleProgressTable: false,
      visibleProgress: false,
      activePanels: [0],
      btnAgregarDocumento: false,
      dialogFormaReenvio: false,
      incluirValid: true,
      consultarEstadoValid: false,
      cancelarValid: false,
      reenviarValid: false,
      imprimirValid: false,
      docViewerDialog: false,
      docViewerUrl: '',
      docViewerTitle: '',
      guardarValid: false,
      search: "",
      snackText: "",
      snackValue: false,
      verPDF: false,
      verXMLFirmado: false,
      verXMLRespuesta: false,
      dateModal: false,
      dates: [],
      datesFormat: [],
      headers: [
        { text: "Código", align: "start", value: "codigoFact" },
        { text: "Cliente", align: "start", value: "clienteNombre" },
        { text: "Fecha", align: "start", value: "fechaString" },
        { text: "Clave", align: "start", value: "clavehacienda" },
        { text: "Estado", align: "start", value: "estadoHacienda" },
        { text: "Total", align: "end", value: "totalComprobante" },
        { text: "", value: "actions", width: "80", sortable: false, align: "center" },
      ],
      headersDetalle: [
        { text: "Cant", align: "start", value: "cantidad", width: "100" },
        { text: "Código", align: "start", value: "articulo" },
        { text: "Descripción", align: "start", value: "descripcion" },
        { text: "Unidad", align: "start", value: "unidad" },
        { text: "Precio", align: "end", value: "montoStr" },
        { text: "Total", align: "end", value: "totalLineaStr" },
        { text: "Acciones", value: "actions", width: "100", sortable: false, align: "center" },
      ],
      items: [],
      actividades: [],
      actividadesCliente: [],
      condicionesVenta: [],
      mediosPago: [],
      sucursales: [],
      terminales: [],
      monedas: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      lineasDetalle: [],
      tipoCambio: [],
      respuestaHacienda: [],
      formas: [],
      dialogArticulos: false,
      detalleEditedIndex: -1,
      detalleEditedItem: {
        cantidad: 0,
        descripcion: "",
        montoStr: "",
        registroMedicamento: "",
        formaFarmaceutica: "",
      },
      detalleDefaultItem: {
        cantidad: 0,
        descripcion: "",
        montoStr: "",
        registroMedicamento: "",
        formaFarmaceutica: "",
      },
      editedIndex: -1,
      modelFactura: {
        comprobante: {},
        comprobanteDetalle: [],
      },
      editedItem: {
        cia: "", codigoFact: "", tipoFact: "02", fecha: new Date().toISOString().slice(0, 10), tipoCambio: 1,
        clienteCod: "", clienteNombre: "", clienteCorreo: "", clientecorreo: "", clienteCedula: "", clienteAreaTel: "", clienteTelefono: "",
        clienteNombreComercial: "", clienteProvincia: "", clienteCanton: "", clienteDistrito: "", clienteBarrio: "",
        clienteDireccion: "", clienteTipoId: "", mensajeHacienda: "", moneda: "CRC", estadoHacienda: "PENDIENTE", clavehacienda: "",
        subtotal: 0, totalDescuentos: 0, totalImpuesto: 0, totalComprobante: 0, sucursal: "", terminal: "",
        observacion: "", usuario: "", nombreTabla: "NotasDebito", 
        fechaHacienda: new Date(), contingenciaFecha: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16), contingenciaFact: "",
        totalIva1: 0, totalIva2: 0, totalIva4: 0, totalIva8: 0, totalIva13: 0, totalIVADevuelto: 0,
        subtotalStr: "0.00", totalDescuentosStr: "0.00", totalImpuestoStr: "0.00", totalComprobanteStr: "0.00",
        totalIva1Str: "0.00", totalIva2Str: "0.00", totalIva4Str: "0.00", totalIva13Str: "0.00", totalIVADevueltoStr: "0.00",
        condicionVenta: "", medioPago: "", codigoActividad: "", clienteCodigoActividad: "", emisorNombre: "", emisorNombreComercial: "",
        emisorTelefono: "", emisorCorreo: "", emisorDireccion: "", emisorCedula: "", emisorTipoId: "",
        totalServGravados: 0, totalServExentos: 0, totalServExonerado: 0, totalMercanciasGravadas: 0,
        totalMercanciasExentas: 0, totalMercExonerada: 0, totalGravado: 0, totalExento: 0, totalExonerado: 0,
        totalVenta: 0, totalVentaNeta: 0, totalOtrosCargos: 0, plazoCredito: 0, estadoDev: 0,
        codigoDev: "", consHacienda: "", consHacReal: "", xml: "", xmlFirmado: "", respuestaHacienda: "",
        cedulaReceptor: "", ordenCompra: "", enviado: false, reenviado: false, NombreTablaReferencia: "", nombreTablaReferencia: "", documentoOrigen: ""
      },
      defaultItem: {
        cia: "", codigoFact: "", tipoFact: "02", fecha: new Date().toISOString().slice(0, 10), tipoCambio: 1,
        clienteCod: "", clienteNombre: "", clienteCorreo: "", clientecorreo: "", clienteCedula: "", clienteAreaTel: "", clienteTelefono: "",
        clienteNombreComercial: "", clienteProvincia: "", clienteCanton: "", clienteDistrito: "", clienteBarrio: "",
        clienteDireccion: "", clienteTipoId: "", mensajeHacienda: "", moneda: "CRC", estadoHacienda: "PENDIENTE", clavehacienda: "",
        subtotal: 0, totalDescuentos: 0, totalImpuesto: 0, totalComprobante: 0, sucursal: "", terminal: "",
        observacion: "", usuario: "", nombreTabla: "NotasDebito",
        fechaHacienda: new Date(), contingenciaFecha: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16), contingenciaFact: "",
        totalIva1: 0, totalIva2: 0, totalIva4: 0, totalIva8: 0, totalIva13: 0, totalIVADevuelto: 0,
        subtotalStr: "0.00", totalDescuentosStr: "0.00", totalImpuestoStr: "0.00", totalComprobanteStr: "0.00",
        totalIva1Str: "0.00", totalIva2Str: "0.00", totalIva4Str: "0.00", totalIva13Str: "0.00", totalIVADevueltoStr: "0.00",
        condicionVenta: "", medioPago: "", codigoActividad: "", clienteCodigoActividad: "", emisorNombre: "", emisorNombreComercial: "",
        emisorTelefono: "", emisorCorreo: "", emisorDireccion: "", emisorCedula: "", emisorTipoId: "",
        totalServGravados: 0, totalServExentos: 0, totalServExonerado: 0, totalMercanciasGravadas: 0,
        totalMercanciasExentas: 0, totalMercExonerada: 0, totalGravado: 0, totalExento: 0, totalExonerado: 0,
        totalVenta: 0, totalVentaNeta: 0, totalOtrosCargos: 0, plazoCredito: 0, estadoDev: 0,
        codigoDev: "", consHacienda: "", consHacReal: "", xml: "", xmlFirmado: "", respuestaHacienda: "",
        cedulaReceptor: "", ordenCompra: "", enviado: false, reenviado: false, NombreTablaReferencia: "", nombreTablaReferencia: "", documentoOrigen: ""
      },
      reenvioInfo: {
        correo: "",
      },
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),
  },

  filters: {
    formatMoney(value) {
      if (!value) return "0.00";
      return parseFloat(value).toLocaleString("es-CR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },

  created() {
    this.setDates();
    this.getData();
    this.getMonedas();
    this.getProvincias();
    this.getActividades();
    this.getCondicionVenta();
    this.getMedioPago();
    this.getFormas();
  },

  methods: {
    getStatusColor(status) {
      if (!status) return "grey";
      const s = status.toUpperCase();
      if (s === "ACEPTADO") return "success";
      if (s === "RECHAZADO") return "error";
      if (s === "PROCESANDO") return "warning";
      if (s === "PENDIENTE") return "info";
      return "grey";
    },

    getData() {
      let me = this;
      me.visibleProgressTable = true;
      axios
        .get(
          "api/Facturacion/GetComprobantes?cia=" +
            this.strCia +
            "&docNombre=NotasDebito&tipoDoc=02" +
            "&fechaInicio=" +
            this.dates[0].toString() +
            "&fechaFin=" +
            this.dates[1].toString()
        )
        .then(function (response) {
          me.items = response.data;
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(() => {
          me.visibleProgressTable = false;
        });
    },

    setDates() {
      var date = new Date();
      var dateInicio = new Date(date.getFullYear(), date.getMonth(), 1);
      this.dates[0] = dateInicio.toISOString().substr(0, 10);
      this.dates[1] = date.toISOString().substr(0, 10);

      var year = date.getFullYear();
      var month = date.getMonth() + 1;
      var dt = date.getDate();
      if (dt < 10) dt = "0" + dt;
      if (month < 10) month = "0" + month;

      var yearInicio = dateInicio.getFullYear();
      var monthInicio = dateInicio.getMonth() + 1;
      var dtInicio = dateInicio.getDate();
      if (dtInicio < 10) dtInicio = "0" + dtInicio;
      if (monthInicio < 10) monthInicio = "0" + monthInicio;

      this.datesFormat = [
        dtInicio + "-" + monthInicio + "-" + yearInicio,
        dt + "-" + month + "-" + year
      ];
    },

    setDatesFormat() {
      if (this.dates.length < 2) return;
      
      const formatOne = (dateStr) => {
        const d = new Date(dateStr.replace(/-/g, "/").replace(/T.+/, ""));
        let day = d.getDate();
        let month = d.getMonth() + 1;
        if (day < 10) day = "0" + day;
        if (month < 10) month = "0" + month;
        return day + "-" + month + "-" + d.getFullYear();
      };

      this.datesFormat = [formatOne(this.dates[0]), formatOne(this.dates[1])];
    },

    mostrarBuscarDocumentos() {
      this.btnAgregarDocumento = true;
    },

    closed() {
      this.btnAgregarDocumento = false;
    },

    selected(item) {
      this.getProvincias();
      // Asegurar que mantenemos el tipo de comprobante correcto para Nota de Débito
      const tipoND = "02";
      this.editedItem = Object.assign({}, this.editedItem, item, { 
        tipoFact: tipoND,
        estadoHacienda: "PENDIENTE",
        mensajeHacienda: ""
      });

      // Asegurar que NombreTablaReferencia esté siempre presente (Dual Case Fix)
      if (item.NombreTablaReferencia) {
        this.editedItem.NombreTablaReferencia = item.NombreTablaReferencia;
        this.editedItem.nombreTablaReferencia = item.NombreTablaReferencia;
      } else if (item.documentoOrigen && item.documentoOrigen.includes("Factura")) {
        this.editedItem.NombreTablaReferencia = "Facturas";
        this.editedItem.nombreTablaReferencia = "Facturas";
      } else if (item.documentoOrigen && item.documentoOrigen.includes("Tiquete")) {
        this.editedItem.NombreTablaReferencia = "Tiquetes";
        this.editedItem.nombreTablaReferencia = "Tiquetes";
      }

      if (!this.editedItem.contingenciaFecha || this.editedItem.contingenciaFecha.includes('0001')) {
        this.editedItem.contingenciaFecha = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      }
      
      this.getCodigoFactura();
      this.getCantones();
      this.getDistritos();
      this.getBarrios();
      this.getFechaActual();
      this.getModeloComprobante();
      
      this.$nextTick(() => {
        this.activePanels = [0, 1, 2, 3]; // Expande Referencia, Receptor, Ítems y Totales
        this.drawerForm = true; 
        this.guardarValid = true; 
        this.btnAgregarDocumento = false; // Cerrar buscador
      });
    },

    getModeloComprobante() {
      let me = this;
      axios
        .get(
          "api/Facturacion/getModeloComprobante?cia=" +
            this.strCia +
            "&clave=" +
            this.editedItem.codigoDev +
            "&docNombre=" +
            this.editedItem.NombreTablaReferencia
        )
        .then(function (response) {
          me.modelFactura = response.data;
          me.lineasDetalle = me.modelFactura.comprobanteDetalle;
          
          if (me.modelFactura.comprobante) {
            me.totales = me.modelFactura.comprobante;
            const comp = me.modelFactura.comprobante;
            
            // Map ALL totals and fields from the source document
            me.editedItem.subtotal = comp.subtotal;
            me.editedItem.totalDescuentos = comp.totalDescuentos;
            me.editedItem.totalImpuesto = comp.totalImpuesto;
            me.editedItem.totalComprobante = comp.totalComprobante;
            me.editedItem.totalIVADevuelto = comp.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = comp.totalOtrosCargos;
            me.editedItem.totalServGravados = comp.totalServGravados;
            me.editedItem.totalServExentos = comp.totalServExentos;
            me.editedItem.totalServExonerado = comp.totalServExonerado;
            me.editedItem.totalMercanciasGravadas = comp.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas = comp.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = comp.totalMercExonerada;
            me.editedItem.totalGravado = comp.totalGravado;
            me.editedItem.totalExento = comp.totalExento;
            me.editedItem.totalExonerado = comp.totalExonerado;
            me.editedItem.totalVenta = comp.totalVenta;
            me.editedItem.totalVentaNeta = comp.totalVentaNeta;
            me.editedItem.moneda = comp.moneda;
            me.editedItem.tipoCambio = comp.tipoCambio;
            
            // Strings for UI
            me.editedItem.subtotalStr = comp.subtotalStr;
            me.editedItem.totalDescuentosStr = comp.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = comp.totalImpuestoStr;
            me.editedItem.totalComprobanteStr = comp.totalComprobanteStr;

            // Client data
            me.editedItem.clienteCod = comp.clienteCod;
            me.editedItem.clienteNombre = comp.clienteNombre;
            me.editedItem.clienteCedula = comp.clienteCedula;
            me.editedItem.clienteCorreo = comp.clienteCorreo;
            me.editedItem.clientecorreo = comp.clientecorreo || comp.clienteCorreo;
            me.editedItem.clienteDireccion = comp.clienteDireccion;
            me.editedItem.clienteProvincia = comp.clienteProvincia;
            me.editedItem.clienteCanton = comp.clienteCanton;
            me.editedItem.clienteDistrito = comp.clienteDistrito;
            me.editedItem.clienteBarrio = comp.clienteBarrio;
            me.getActividadesClienteDefecto(comp.clienteCedula);
          }

          me.changeCantidad();

          me.$nextTick(() => {
            me.drawerForm = true;
            me.activePanels = [0, 1, 2, 3]; // Expande Emisor, Receptor, Ítems y Totales
          });
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getActividadesClienteDefecto(cedulaCliente) {
      if (!cedulaCliente) return;
      let me = this;
      axios.get(`api/Clientes/getActividadesByCliente?cedulaCia=${this.strCia}&cedulaCliente=${cedulaCliente}`)
        .then(res => {
          me.actividadesCliente = res.data || [];
          if (res.data && res.data.length > 0) {
            const defecto = res.data.find(a => a.esDefecto) || res.data[0];
            if (!me.editedItem.clienteCodigoActividad)
              me.editedItem.clienteCodigoActividad = defecto.codigo;
          }
        });
    },

    getModeloComprobanteND() {
      let me = this;
      axios
        .get(
          "api/Facturacion/getModeloComprobante?cia=" +
            this.strCia +
            "&clave=" +
            this.editedItem.clavehacienda +
            "&docNombre=NotasDebito"
        )
        .then(function (response) {
          me.modelFactura = response.data;
          me.editedItem = Object.assign({}, me.editedItem, me.modelFactura.comprobante);
          me.lineasDetalle = me.modelFactura.comprobanteDetalle;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    changeCantidad() {
      if (this.guardarValid) {
        let me = this;
        axios
          .post("api/Facturacion/realizarCalculosComprobante", this.lineasDetalle)
          .then(function (response) {
            me.lineasDetalle = response.data.comprobanteDetalle;
            me.totales = response.data.comprobante;
            
            // Sync all numeric totals
            me.editedItem.subtotal = me.totales.subtotal;
            me.editedItem.totalDescuentos = me.totales.totalDescuentos;
            me.editedItem.totalImpuesto = me.totales.totalImpuesto;
            me.editedItem.totalComprobante = me.totales.totalComprobante;
            me.editedItem.totalServGravados = me.totales.totalServGravados;
            me.editedItem.totalServExentos = me.totales.totalServExentos;
            me.editedItem.totalServExonerado = me.totales.totalServExonerado;
            me.editedItem.totalMercanciasGravadas = me.totales.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas = me.totales.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = me.totales.totalMercExonerada;
            me.editedItem.totalGravado = me.totales.totalGravado;
            me.editedItem.totalExento = me.totales.totalExento;
            me.editedItem.totalExonerado = me.totales.totalExonerado;
            me.editedItem.totalVenta = me.totales.totalVenta;
            me.editedItem.totalVentaNeta = me.totales.totalVentaNeta;
            me.editedItem.totalIVADevuelto = me.totales.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = me.totales.totalOtrosCargos;
            me.editedItem.totalIva1 = me.totales.totalIva1;
            me.editedItem.totalIva2 = me.totales.totalIva2;
            me.editedItem.totalIva4 = me.totales.totalIva4;
            me.editedItem.totalIva13 = me.totales.totalIva13;

            // Sync all string totals for UI
            me.editedItem.subtotalStr = me.totales.subtotalStr;
            me.editedItem.totalDescuentosStr = me.totales.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = me.totales.totalImpuestoStr;
            me.editedItem.totalComprobanteStr = me.totales.totalComprobanteStr;
            me.editedItem.totalIva1Str = me.totales.totalIva1Str;
            me.editedItem.totalIva2Str = me.totales.totalIva2Str;
            me.editedItem.totalIva4Str = me.totales.totalIva4Str;
            me.editedItem.totalIva13Str = me.totales.totalIva13Str;
            me.editedItem.totalIVADevueltoStr = me.totales.totalIVADevueltoStr;
          })
          .catch(function (error) {
            console.log(error);
          });
      }
    },

    validarLicencia() {
      this.incluir();
    },

    incluir() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.lineasDetalle = [];
      this.getCia();
      this.incluirValid = false;
      this.guardarValid = true;
      this.cancelarValid = true;
      this.btnAgregarDocumento = true; // Sigue la lógica de Crédito de abrir búsqueda al dar nueva nota
      this.drawerForm = true;
      this.activePanels = [0];
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.getModeloComprobanteND();
      this.drawerForm = true;
      this.incluirValid = true;
      this.guardarValid = false;
      this.cancelarValid = true;
      this.btnAgregarDocumento = false;
      this.activePanels = [0, 1, 2, 3];

      const status = this.editedItem.estadoHacienda;
      if (status === "ACEPTADO" || status === "RECHAZADO") {
        this.verPDF = true;
        this.verXMLFirmado = true;
        this.verXMLRespuesta = true;
        this.reenviarValid = true;
      } else {
        this.verPDF = false;
        this.verXMLFirmado = false;
        this.verXMLRespuesta = false;
        this.reenviarValid = false;
      }

      if (status === "PROCESANDO") {
        this.consultarEstadoValid = true;
      }
    },

    detalleEditItem(item) {
      this.detalleEditedIndex = this.lineasDetalle.indexOf(item);
      this.detalleEditedItem = Object.assign({}, item);
      this.dialogArticulos = true;
    },

    saveDetalleArticulo() {
      if (this.detalleEditedIndex > -1) {
        Object.assign(this.lineasDetalle[this.detalleEditedIndex], this.detalleEditedItem);
        this.changeCantidad();
      }
      this.close();
    },

    close() {
      this.dialogArticulos = false;
      this.$nextTick(() => {
        this.detalleEditedItem = Object.assign({}, this.detalleDefaultItem);
        this.detalleEditedIndex = -1;
      });
    },

    cerrar() {
      this.drawerForm = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.lineasDetalle = [];
        this.incluirValid = true;
        this.guardarValid = false;
        this.cancelarValid = false;
        this.activePanels = [0]; // Solo Referencia al inicio
      });
    },

    getCodigoFactura() {
      let me = this;
      axios
        .get(
          "api/Facturacion/getComprobanteConsInterno?cia=" +
            this.strCia +
            "&docNombre=NotasDebito&tipoDoc=ND"
        )
        .then(function (response) {
          me.editedItem.codigoFact = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getFechaActual() {
      let me = this;
      axios.get("api/General/getFechaActualString").then(function (response) {
        me.editedItem.fecha = response.data;
      });
    },

    getCantones() {
      axios.get("api/General/getCantones?provincia=" + this.editedItem.clienteProvincia)
        .then(res => this.cantones = res.data);
    },

    getDistritos() {
      axios.get(`api/General/getDistritos?provincia=${this.editedItem.clienteProvincia}&canton=${this.editedItem.clienteCanton}`)
        .then(res => this.distritos = res.data);
    },

    getBarrios() {
      axios.get(`api/General/getBarrios?provincia=${this.editedItem.clienteProvincia}&canton=${this.editedItem.clienteCanton}&distrito=${this.editedItem.clienteDistrito}`)
        .then(res => this.barrios = res.data);
    },

    save() {
      let me = this;
      this.visibleProgress = true;
      
      const now = new Date();
      this.editedItem.cia = this.strCia;
      this.editedItem.usuario = this.user.id;
      this.editedItem.fecha = now;

      if (!this.editedItem.contingenciaFecha || this.editedItem.contingenciaFecha.includes('0001')) {
        this.editedItem.contingenciaFecha = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      }

      // Asegurar NombreTablaReferencia justo antes de enviar (Dual Case Fix)
      this.editedItem.nombreTabla = "NotasDebito";
      if (!this.editedItem.NombreTablaReferencia) {
        if (this.editedItem.documentoOrigen && this.editedItem.documentoOrigen.includes("Factura")) {
          this.editedItem.NombreTablaReferencia = "Facturas";
          this.editedItem.nombreTablaReferencia = "Facturas";
        } else if (this.editedItem.documentoOrigen && this.editedItem.documentoOrigen.includes("Tiquete")) {
          this.editedItem.NombreTablaReferencia = "Tiquetes";
          this.editedItem.nombreTablaReferencia = "Tiquetes";
        }
      } else {
        this.editedItem.nombreTablaReferencia = this.editedItem.NombreTablaReferencia;
      }

      const payload = {
        comprobante: this.editedItem,
        comprobanteDetalle: this.lineasDetalle
      };

      console.log("PAYLOAD ND:", payload); // Debug for user if fails again

      axios
        .post("api/Facturacion/insertComprobante", payload)
        .then(function (response) {
          me.visibleProgress = false;
          me.respuestaHacienda = response.data;

          me.editedItem.clavehacienda = me.respuestaHacienda.claveHacienda;
          me.editedItem.estadoHacienda = me.respuestaHacienda.eInvoiceStatus;
          me.editedItem.mensajeHacienda = me.respuestaHacienda.details;

          const status = me.editedItem.estadoHacienda;
          if (status === "ACEPTADO" || status === "RECHAZADO") {
            me.verPDF = true;
            me.verXMLFirmado = true;
            me.verXMLRespuesta = true;
            me.imprimirValid = true;
            me.reenviarValid = true;
          }

          if (status === "PROCESANDO") {
            me.consultarEstadoValid = true;
          }

          // Auto-expandir panel de Hacienda (4) y mantener los otros útiles abiertos
          me.activePanels = [2, 3, 4]; 
          
          me.getData();
        })
        .catch(function (error) {
          console.log(error);
          me.visibleProgress = false;
        });
    },

    deleteItem(item) {
      const index = this.lineasDetalle.indexOf(item);
      if (index > -1) {
        this.lineasDetalle.splice(index, 1);
        this.changeCantidad();
      }
    },

    consultarEstadoHacienda() {
      let me = this;
      me.visibleProgress = true;
      axios
        .post("api/Facturacion/obtenerRespuestaHacienda", this.editedItem)
        .then(function (response) {
          me.respuestaHacienda = response.data;
          me.editedItem.estadoHacienda = me.respuestaHacienda.eInvoiceStatus;
          me.editedItem.mensajeHacienda = me.respuestaHacienda.details;
          
          if (me.editedItem.estadoHacienda !== "PROCESANDO") {
            me.consultarEstadoValid = false;
          }
          me.getData();
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(() => {
          me.visibleProgress = false;
        });
    },

    reenviar() {
      this.reenvioInfo.correo = this.editedItem.clienteCorreo || this.editedItem.clientecorreo;
      this.dialogFormaReenvio = true;
    },

    confirmarReenvio() {
      let me = this;
      me.visibleProgress = true;
      this.editedItem.clientecorreo = this.reenvioInfo.correo;
      axios.post("api/Facturacion/reenviarCorreo", this.editedItem)
        .then(function (response) {
          me.snackText = response.data;
          me.snackValue = true;
          me.dialogFormaReenvio = false;
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(() => me.visibleProgress = false);
    },

    openDocViewer(url, title) {
      this.docViewerUrl = url;
      this.docViewerTitle = title;
      this.docViewerDialog = true;
    },

    abrirEnNuevaPestana() {
      window.open(this.docViewerUrl, '_blank');
    },

    getReport() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=pdf`,
        'PDF — Nota de Débito'
      );
    },

    getXMLFirmado() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=signed`,
        'XML Enviado — Nota de Débito'
      );
    },

    getXMLRespuesta() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=response`,
        'Respuesta XML — Nota de Débito'
      );
    },

    // API Helpers
    getCia() {
      let me = this;
      axios.get("api/Cias/getCia?ciaId=" + this.strCia)
        .then(function (response) {
          const cia = response.data;
          me.editedItem.emisorNombre = cia.nombre;
          me.editedItem.emisorNombreComercial = cia.nombreComercial;
          me.editedItem.codigoActividad = cia.codigoActividad;
          me.editedItem.emisorTelefono = cia.telefono;
          me.editedItem.emisorCorreo = cia.correo;
          me.editedItem.emisorDireccion = cia.direccion;
          me.editedItem.moneda = cia.moneda;
          me.editedItem.cia = cia.cedula;
          me.editedItem.sucursal = cia.sucursal;
          me.editedItem.terminal = cia.terminal;
        });
    },

    getMonedas() { axios.get("api/General/getMonedas").then(r => this.monedas = r.data); },
    getProvincias() { axios.get("api/General/getProvincias").then(r => this.provincias = r.data); },
    getActividades() { axios.get("api/General/getActividadesByCia?cia=" + this.strCia).then(r => this.actividades = r.data); },
    getCondicionVenta() { axios.get("api/General/getCondicionVenta").then(r => this.condicionesVenta = r.data); },
    getMedioPago() { axios.get("api/General/getMediosPago").then(r => this.mediosPago = r.data); },
    getFormas() { axios.get("api/General/getFormas").then(r => this.formas = r.data); },

    getEstadoColor(status) {
      return this.getStatusColor(status);
    }
  }
};
</script>

<style scoped>
/* Tabla principal */
.modern-table { background: transparent !important; }
.clickable-rows >>> tbody tr { cursor: pointer; transition: background-color 0.2s; }
.clickable-rows >>> tbody tr:hover { background-color: rgba(5, 150, 105, 0.05) !important; }
.modern-table >>> thead th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.06em;
  border-bottom: 2px solid #f1f5f9 !important;
}
.theme--dark .modern-table >>> thead th {
  background-color: #1e293b !important;
  color: #94a3b8 !important;
  border-bottom: 2px solid #334155 !important;
}

/* Tabla de detalle */
.detail-table-modern { background: transparent !important; }
.detail-table-modern >>> thead th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  letter-spacing: 0.06em;
  border-bottom: 2px solid #f1f5f9 !important;
  height: 40px !important;
}
.detail-table-modern >>> tbody td {
  border-bottom: 1px solid #f1f5f9 !important;
  height: 40px !important;
}
.theme--dark .detail-table-modern { background: transparent !important; }
.theme--dark .detail-table-modern >>> thead th {
  background-color: #1e293b !important;
  color: #94a3b8 !important;
  border-bottom: 2px solid #334155 !important;
}
.theme--dark .detail-table-modern >>> tbody td {
  border-bottom: 1px solid #334155 !important;
}

/* Drawer */
.form-drawer { background: #ffffff !important; }
.theme--dark .form-drawer { background: #0f172a !important; }

/* Drawer header */
.drawer-header {
  height: 60px;
  border-radius: 0 0 0 20px;
  background: linear-gradient(135deg, #047857 0%, #059669 60%, #10b981 100%) !important;
}

/* Expansion panels */
.modern-panels >>> .v-expansion-panel { border: 1px solid rgba(0,0,0,0.07) !important; }
.theme--dark .modern-panels >>> .v-expansion-panel { border: 1px solid rgba(255,255,255,0.07) !important; }
.modern-panels >>> .v-expansion-panel--active {
  border: 1px solid rgba(5, 150, 105, 0.3) !important;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.07) !important;
  margin-bottom: 2px !important;
}
.modern-panels >>> .v-expansion-panel + .v-expansion-panel { margin-top: 2px !important; }
.modern-panels >>> .v-expansion-panel-header {
  font-size: 0.875rem !important;
  letter-spacing: -0.01em;
  padding: 0 12px !important;
  min-height: 40px !important;
}
.modern-panels >>> .v-expansion-panel-content__wrap { padding: 4px 4px 6px !important; }
.panel-content-compact { padding: 4px 4px 6px !important; }

/* Botones de acción */
.action-btn { background: rgba(5, 150, 105, 0.05); transition: all 0.2s; }
.action-btn:hover { background: rgba(5, 150, 105, 0.15); transform: scale(1.1); }

/* Inputs compactos */
.compact-input >>> input {
  padding: 0px !important;
  text-align: center !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  height: 28px !important;
  min-width: 40px !important;
  max-width: 60px !important;
}
.compact-input >>> .v-input__slot { padding: 0 4px !important; border-radius: 8px !important; min-height: 28px !important; }
.compact-input >>> .v-input__control { min-height: 38px !important; }
.compact-input >>> input::-webkit-outer-spin-button,
.compact-input >>> input::-webkit-inner-spin-button { -webkit-appearance: none !important; margin: 0 !important; }
.theme--dark .compact-input >>> input { color: #ffffff !important; }
.theme--light .compact-input >>> input { color: #000000 !important; }
.compact-input-table >>> .v-input__control { width: 80px !important; }
.right-input >>> input { text-align: right; font-weight: bold; }

/* Visor de documentos */
.doc-viewer-toolbar {
  background: linear-gradient(135deg, #047857 0%, #059669 60%, #10b981 100%);
  min-height: 44px;
  flex-shrink: 0;
}

/* ===== NDE DESIGN SYSTEM ===== */
.nde-container { background-color: #f1f5f9; min-height: 100vh; }
.theme--dark.nde-container { background-color: #0f172a; }

.nde-toolbar { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.theme--dark .nde-toolbar { border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
.nde-toolbar-title { font-size: 0.95rem !important; letter-spacing: 1.5px; }

.nde-btn { border-radius: 4px !important; text-transform: uppercase; letter-spacing: 0.8px; font-size: 0.75rem !important; }

.nde-table-card { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.nde-table { background: transparent !important; }
.nde-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.nde-table >>> tbody tr { cursor: pointer; }
.nde-table >>> tbody tr:hover { background-color: rgba(55,71,79,0.05) !important; }
.theme--dark .nde-table >>> tbody tr:hover { background-color: rgba(255,255,255,0.05) !important; }

.nde-search >>> .v-input__slot { border-radius: 4px !important; }

.nde-drawer >>> .v-navigation-drawer__content { background-color: #ffffff !important; }
.theme--dark .nde-drawer >>> .v-navigation-drawer__content { background-color: #0f172a !important; }

.nde-drawer-header { background-color: #37474f; flex-shrink: 0; }
.nde-drawer-header--dark { background-color: #263238 !important; }

.nde-drawer-footer { background-color: #f8fafc; border-top: 1px solid rgba(0,0,0,0.08); flex-shrink: 0; }
.nde-drawer-footer--dark { background-color: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }
</style>